import { Link } from "react-router-dom";
import { PawPrint, Smartphone, Download, Share, MoreVertical } from "lucide-react";

function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 rounded-full bg-fern text-white font-heading font-semibold flex items-center justify-center shrink-0">
        {n}
      </div>
      <div className="flex-1 pt-1">
        <p className="font-medium text-ink mb-1">{title}</p>
        <div className="text-sm text-muted-foreground space-y-1">{children}</div>
      </div>
    </div>
  );
}

export default function Install() {
  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="border-b border-border bg-white/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="install-home">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Shelter Focus</span>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-ink">Sign in</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16 space-y-10">
        <div>
          <div className="w-14 h-14 rounded-2xl bg-fern/10 text-fern flex items-center justify-center mb-4">
            <Smartphone className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <p className="label-caps mb-2">Volunteer app</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">Add Shelter Focus to your phone</h1>
          <p className="text-muted-foreground max-w-xl">
            Shelter Focus lives in your web browser today. Add it to your home screen and it works exactly like a native app —
            full-screen, tap to open, offline-safe, no app store download needed.
          </p>
        </div>

        {/* iOS */}
        <section className="tactile-card border-0 p-6 space-y-5" data-testid="install-ios">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🍎</span>
            <h2 className="font-heading text-2xl font-semibold">iPhone &amp; iPad</h2>
          </div>
          <p className="text-sm text-muted-foreground">Works in <strong className="text-ink">Safari</strong>. Chrome for iOS can&apos;t install PWAs — please use Safari.</p>
          <div className="space-y-4">
            <Step n={1} title="Open shelterfocus.com in Safari">
              <p>Tap the address bar and paste the link, or tap the link your admin sent.</p>
            </Step>
            <Step n={2} title="Tap the Share button">
              <p className="flex items-center gap-2">
                Look for the <Share className="w-4 h-4 inline text-fern" strokeWidth={1.5} /> icon at the bottom of the screen
                (or top-right on iPad).
              </p>
            </Step>
            <Step n={3} title="Scroll and tap &ldquo;Add to Home Screen&rdquo;">
              <p>You&apos;ll see a preview with the paw print icon. Tap <strong className="text-ink">Add</strong> in the top-right.</p>
            </Step>
            <Step n={4} title="You&apos;re done">
              <p>Shelter Focus is now on your home screen. Tap it to open — it&apos;ll look and feel like any other app.</p>
            </Step>
          </div>
        </section>

        {/* Android */}
        <section className="tactile-card border-0 p-6 space-y-5" data-testid="install-android">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🤖</span>
            <h2 className="font-heading text-2xl font-semibold">Android</h2>
          </div>
          <p className="text-sm text-muted-foreground">Works in <strong className="text-ink">Chrome</strong> (and most Chromium browsers).</p>
          <div className="space-y-4">
            <Step n={1} title="Open shelterfocus.com in Chrome">
              <p>Chrome may show an &ldquo;Install app&rdquo; banner at the top or bottom — tap that if you see it and skip to step 4.</p>
            </Step>
            <Step n={2} title="Tap the browser menu">
              <p className="flex items-center gap-2">
                Look for the <MoreVertical className="w-4 h-4 inline text-fern" strokeWidth={1.5} /> three-dot menu at the top-right.
              </p>
            </Step>
            <Step n={3} title="Tap &ldquo;Install app&rdquo; or &ldquo;Add to Home screen&rdquo;">
              <p>Chrome may call it either name depending on version. Confirm when it asks.</p>
            </Step>
            <Step n={4} title="Open from your home screen">
              <p>Shelter Focus lives with your other apps now. Tap the paw print to open.</p>
            </Step>
          </div>
          <a
            href="https://support.google.com/chrome/answer/9658361?hl=en&co=GENIE.Platform%3DAndroid"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-fern hover:underline text-sm mt-2"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2} />
            Chrome official install help
          </a>
        </section>

        {/* Desktop */}
        <section className="tactile-card border-0 p-6 space-y-3" data-testid="install-desktop">
          <div className="flex items-center gap-2">
            <span className="text-2xl">💻</span>
            <h2 className="font-heading text-2xl font-semibold">Mac &amp; Windows</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            In Chrome or Edge, click the install icon in the address bar (right side, looks like a monitor with a down-arrow)
            or open the menu and pick <strong className="text-ink">Install Shelter Focus…</strong>.
            Safari on macOS Sonoma+ supports this via File → Add to Dock.
          </p>
        </section>

        <div className="border-t border-border pt-6">
          <p className="text-sm text-muted-foreground mb-3">Trouble with the install? Just keep using the site in your browser — everything works there too. Bookmark <strong className="text-ink">shelterfocus.com</strong> for one-tap access.</p>
          <Link to="/login" className="text-fern hover:underline" data-testid="install-back-to-login">← Back to sign in</Link>
        </div>
      </main>
    </div>
  );
}
